import type { UseQueryResult } from '@tanstack/react-query'
import { replaceEqualDeep, useQueries, useQueryClient } from '@tanstack/react-query'
import { useMemo, useRef } from 'react'

import { useGlobalState } from '@/hooks/useGlobalState'
import type { GetResourcesResponse, GetStepsResponse, ProcessProgress, ResourceType } from '@/services/api/api.types'

export type StepWithValidation = GetStepsResponse[0] & {
  valid: boolean
  contentType?: ResourceType
  // S3 key for the source file's thumbnail. Drives the table's thumbnail column preview.
  thumbnail?: string | null
  inProcess?: boolean | null
  processProgress?: ProcessProgress['processProgress']
  processProgressError?: string
  transcodeCompleted?: boolean
  transcodeSuccess?: boolean
}

type CombinedResourceResult = {
  data: GetResourcesResponse | undefined
  isSuccess: boolean
  isError: boolean
  error: Error | null
}

type CombinedContentResult = {
  data:
    | {
        inProcess?: boolean
        transcodeCompleted?: boolean
        transcodeSuccess?: boolean
        processProgress?: ProcessProgress['processProgress']
      }
    | undefined
  isSuccess: boolean
}

// Module-scoped so the combine identity is stable; inline fns defeat React
// Query's combine memoization + structural sharing.
const combineResourceResults = (results: UseQueryResult[]): CombinedResourceResult[] =>
  results.map(r => ({
    data: r.data as GetResourcesResponse | undefined,
    isSuccess: r.isSuccess,
    isError: r.isError,
    error: r.error,
  }))

const combineContentResults = (results: UseQueryResult[]): CombinedContentResult[] =>
  results.map(r => ({
    data: r.data as CombinedContentResult['data'],
    isSuccess: r.isSuccess,
  }))

export type StepsRefetchOptions = {
  /**
   * If provided, also invalidate the resources/content-meta queries for this
   * specific step. Omit to only refresh the base steps list — new steps' child
   * queries will auto-fetch when `useQueries` mounts them, and existing steps'
   * caches stay intact (their own pollers handle staleness).
   */
  stepId?: string
}

export interface StepsWithValidationResult {
  data: StepWithValidation[]
  isLoading: boolean
  isError: boolean
  error: Error | null
  isSuccess: boolean
  refetch: (options?: StepsRefetchOptions) => Promise<{ data?: StepWithValidation[] }>
}

/**
 * Custom hook that calculates step validation status by batching resource queries.
 * Uses React Query's useQueries to fetch resources for all steps in parallel with caching.
 */
export function useStepsWithValidation(stepsQuery: {
  data?: GetStepsResponse
  isSuccess: boolean
  isLoading: boolean
  isError: boolean
  error: Error | null
  refetch: () => Promise<{ data?: GetStepsResponse }>
}): StepsWithValidationResult {
  const { api } = useGlobalState()
  const queryClient = useQueryClient()

  // Backs the replaceEqualDeep structural sharing on the memo output below.
  const sharedStepsRef = useRef<StepWithValidation[]>([])

  const steps = stepsQuery.data ?? []

  // Fetch all resources (including source files) directly from endpoint.
  // `combine` with structural sharing ensures the returned value only gets a
  // new reference when actual query data changes — not on every render cycle.
  // `notifyOnChangeProps` limits which query state changes trigger re-renders.
  const resourceResults = useQueries({
    queries: steps.map(step => ({
      queryKey: ['step', 'resources', 'all', step.stepId],
      queryFn: async () => {
        return api.request<GetResourcesResponse>('/cm/get_resources', 'get', {
          step_id: step.stepId,
        })
      },
      enabled: !!step.stepId && stepsQuery.isSuccess,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      notifyOnChangeProps: ['data', 'error', 'isSuccess'] as Array<keyof UseQueryResult>,
    })),
    combine: combineResourceResults,
  })

  // Fetch content metadata per step for transcoding status.
  // Same combine + notifyOnChangeProps strategy as resource queries.
  const contentResults = useQueries({
    queries: steps.map(step => ({
      queryKey: ['step', 'content', 'meta', step.stepId],
      queryFn: () => api.getContentMeta({ step_id: step.stepId }),
      enabled: !!step.stepId && stepsQuery.isSuccess,
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      notifyOnChangeProps: ['data', 'error', 'isSuccess'] as Array<keyof UseQueryResult>,
      refetchInterval: (query: { state: { data?: unknown } }) => {
        const data = query.state.data as
          | { inProcess?: boolean; transcodeCompleted?: boolean; processProgress?: ProcessProgress['processProgress'] }
          | undefined
        if (data?.processProgress?.error) {
          return false
        }
        if (data && (data.inProcess === true || data.transcodeCompleted === false)) {
          return 5000
        }
        return false
      },
    })),
    combine: combineContentResults,
  })

  // combine refs churn every render, so this memo recomputes constantly;
  // replaceEqualDeep collapses deeply-equal rebuilds back to the prior reference
  // (and reuses unchanged row refs) to avoid a full table reconcile each render.
  const stepsWithValidation = useMemo(() => {
    if (!stepsQuery.isSuccess) {
      return []
    }

    const built = steps.map((step, index) => {
      const resourceResult = resourceResults[index]
      const contentResult = contentResults[index]

      const contentInProcess = contentResult?.data?.inProcess
      const inProcess = step.inProcess === true || contentInProcess === true
      const processProgress = contentResult?.data?.processProgress
      const rawError = processProgress?.error
      const processProgressError = typeof rawError === 'string' ? rawError : rawError?.message
      const transcodeCompleted = contentResult?.data?.transcodeCompleted
      const transcodeSuccess = contentResult?.data?.transcodeSuccess

      if (!resourceResult?.isSuccess) {
        return {
          ...step,
          valid: !!step.name,
          inProcess,
          processProgress,
          processProgressError,
          transcodeCompleted,
          transcodeSuccess,
        }
      }

      const allResources = resourceResult.data ?? []
      const sourceFile = allResources.find(r => r.isSourceFile)

      return {
        ...step,
        valid: !!step.name,
        contentType: sourceFile?.type,
        thumbnail: sourceFile?.thumbnailS3Key ?? null,
        inProcess,
        processProgress,
        processProgressError,
        transcodeCompleted,
        transcodeSuccess,
      }
    })

    const shared = replaceEqualDeep(sharedStepsRef.current, built) as StepWithValidation[]
    sharedStepsRef.current = shared
    return shared
  }, [steps, resourceResults, contentResults, stepsQuery.isSuccess])

  // Stable refetch function that doesn't change reference across renders.
  // All mutable dependencies are accessed via refs so the closure never goes stale.
  //
  // IMPORTANT: this does NOT invalidate every step's child queries by default.
  // Doing so used to fan out into 2N parallel requests (resources + content-meta
  // for every step) on every step add/update/reorder/save, which crashed the
  // tab for modules with hundreds of files. Callers that know which step
  // changed should pass `{ stepId }`; callers that just want a fresh ordering
  // (e.g. reorder) should pass nothing.
  const refetchStepsRef = useRef(stepsQuery.refetch)
  refetchStepsRef.current = stepsQuery.refetch
  const refetch = useRef(async (options?: StepsRefetchOptions) => {
    const stepsResult = await refetchStepsRef.current()

    if (options?.stepId) {
      queryClient.invalidateQueries({ queryKey: ['step', 'resources', 'all', options.stepId] })
      queryClient.invalidateQueries({ queryKey: ['step', 'content', 'meta', options.stepId] })
    }

    if (stepsResult.data) {
      const enhancedSteps = stepsResult.data.map(step => ({
        ...step,
        valid: !!step.name,
      }))
      return { data: enhancedSteps }
    }

    return { data: undefined }
  }).current

  // Derive error state from resource results (already structurally shared).
  const resourceError = useMemo(() => {
    const hasError = resourceResults.some(q => q.isError)
    const firstError = resourceResults.find(q => q.error)?.error ?? null
    return { hasError, firstError }
  }, [resourceResults])

  // Only recompute result when data, loading, or error state actually changes.
  // Background refetch toggles (isFetching) are intentionally excluded — each
  // query's isFetching toggle was causing a full tree re-render with many steps.
  const result = useMemo((): StepsWithValidationResult => {
    return {
      data: stepsWithValidation,
      isLoading: stepsQuery.isLoading,
      isError: stepsQuery.isError || resourceError.hasError,
      error: stepsQuery.error || resourceError.firstError,
      isSuccess: stepsQuery.isSuccess,
      refetch,
    }
  }, [
    stepsWithValidation,
    stepsQuery.isLoading,
    stepsQuery.isError,
    stepsQuery.error,
    stepsQuery.isSuccess,
    resourceError,
    refetch,
  ])

  return result
}
